import React, { useMemo, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button, NumberBox } from "devextreme-react";
import {
    DataGrid,
    Column,
    Scrolling,
    LoadPanel,
    FilterRow,
    GroupPanel,
    Grouping,
    Selection,
    Toolbar,
    Item,
    Lookup,
    Export,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import axios from "axios";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import routes from "../../../../../assets/apiRoutes.json";

const recordTypes = [
    { ID: "I", Text: "Inventory" },
    { ID: "N", Text: "Non Inventory" },
];

function TransactionDetailGridPopup(props) {
    const { showPopup = {}, setShowPopup, onDataChanged, setLoadPanelVisible, oidc } = props;
    const visible = showPopup.visible === true;
    const transactionHeaderId = showPopup.transactionHeaderId;
    const accessToken = oidc?.user?.access_token;

    const dataGridRef = useRef(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showUnitPricePopup, setShowUnitPricePopup] = useState(false);
    const [unitPriceInput, setUnitPriceInput] = useState(0);
    const [busy, setBusy] = useState(false);
    const [isApplyingUnitPrice, setIsApplyingUnitPrice] = useState(false);

    const dataSource = useMemo(() => {
        if (!transactionHeaderId || !accessToken) return null;
        return createStore({
            key: "Rowid",
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetTransactionDetails}`,
            loadParams: {
                RTransactionheader: transactionHeaderId,
            },
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                    Key: transactionHeaderId,
                };
            },
        });
    }, [transactionHeaderId, accessToken]);

    const handleHiding = useCallback(() => {
        setSelectedRowKeys([]);
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const refreshGridAndNotifyParent = useCallback(() => {
        const grid = dataGridRef.current?.instance();
        if (!grid) return;
        grid.refresh();
        if (typeof onDataChanged === "function") onDataChanged();
    }, [onDataChanged]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedRowKeys(e.selectedRowKeys || []);
    }, []);

    const deleteSelected = useCallback(() => {
        if (!selectedRowKeys.length) return;
        confirm("<i>Are you sure you want to delete the selected lines?</i>", "Delete").then((ok) => {
            if (!ok) return;
            setBusy(true);
            const rowIds = selectedRowKeys.map((k) => String(k));
            axios
                .post(
                    `${window._env_.REACT_APP_APIS}/${routes.OM.DeleteTransactionDetails}`,
                    rowIds,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                        },
                    }
                )
                .then(() => {
                    setSelectedRowKeys([]);
                    notify({ message: "Selected lines deleted.", width: 280 }, "success", 3000);
                    refreshGridAndNotifyParent();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || err.message || "Delete failed.";
                    alert(msg, "Error");
                })
                .finally(() => setBusy(false));
        });
    }, [selectedRowKeys, accessToken, refreshGridAndNotifyParent]);

    const openUnitPricePopup = useCallback(() => {
        if (!selectedRowKeys.length) return;
        setUnitPriceInput(0);
        setShowUnitPricePopup(true);
    }, [selectedRowKeys.length]);

    const applyUnitPrice = useCallback(() => {
        if (!selectedRowKeys.length || transactionHeaderId == null) return;
        const price = Number(unitPriceInput);
        if (Number.isNaN(price)) {
            alert("Enter a valid unit price.", "Error");
            return;
        }
        setIsApplyingUnitPrice(true);
        if (typeof setLoadPanelVisible === "function") {
            setLoadPanelVisible(true);
        }
        axios
            .post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.BulkUpdateTransactionDetailsUnitPrice}`,
                {
                    transactionHeaderId,
                    detailRowIds: selectedRowKeys.map((k) => String(k)),
                    unitPrice: price,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    },
                }
            )
            .then((res) => {
                const body = res.data;
                const failed = body && (body.Success === false || body.success === false);
                if (failed) {
                    alert(body.message || "Update failed.", "Error");
                    return;
                }
                setShowUnitPricePopup(false);
                notify({ message: "Unit price updated for selected lines.", width: 320 }, "success", 3000);
                refreshGridAndNotifyParent();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || err.message || "Update failed.";
                alert(msg, "Error");
            })
            .finally(() => {
                setIsApplyingUnitPrice(false);
                if (typeof setLoadPanelVisible === "function") {
                    setLoadPanelVisible(false);
                }
            });
    }, [
        selectedRowKeys,
        transactionHeaderId,
        unitPriceInput,
        accessToken,
        refreshGridAndNotifyParent,
        setLoadPanelVisible,
    ]);

    if (!visible) return null;

    return (
        <>
            <Popup
                visible={true}
                onHiding={handleHiding}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title={showPopup.title ?? "OM Transaction Detail Grid"}
                container="#root"
                width="100vw"
                height="100vh"
            >
                {!dataSource ? (
                    <div style={{ padding: 16 }}>Missing transaction or session.</div>
                ) : (
                    <DataGrid
                        ref={dataGridRef}
                        dataSource={dataSource}
                        onExporting={(e) => {
                            if (window.handleDevExtremeDataGridExport) {
                                window.handleDevExtremeDataGridExport(e);
                            }
                        }}
                        onSelectionChanged={onSelectionChanged}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        allowColumnReordering={true}
                        hoverStateEnabled={true}
                        height="calc(100vh - 120px)"
                        showColumnLines={true}
                        showRowLines={true}
                        rowAlternationEnabled={true}
                        paging={false}
                    >
                        <Export enabled={true} allowExportSelectedData={true} />
                        <Scrolling mode="standard" />
                        <LoadPanel enabled={true} />
                        <FilterRow visible={true} />
                        <GroupPanel visible={true} />
                        <Grouping autoExpandAll={false} />
                        <Selection mode="multiple" showCheckBoxesMode="always" />
                        <Toolbar>
                            <Item location="after">
                                <Button
                                    text="Enter unit price"
                                    type="default"
                                    disabled={!selectedRowKeys.length || busy || isApplyingUnitPrice}
                                    onClick={openUnitPricePopup}
                                />
                            </Item>
                            <Item location="after">
                                <Button
                                    icon="trash"
                                    disabled={!selectedRowKeys.length || busy || isApplyingUnitPrice}
                                    onClick={deleteSelected}
                                />
                            </Item>
                        </Toolbar>
                        <Column dataField="Rowid" visible={false} />
                        <Column dataField="RecordType" caption="Type" allowEditing={false}>
                            <Lookup dataSource={recordTypes} valueExpr="ID" displayExpr="Text" />
                        </Column>
                        <Column dataField="WarehouseName" caption="Warehouse" />
                        <Column dataField="ItemCode" caption="Item" />
                        <Column dataField="ItemDesc1" caption="Desc 1 - Item" />
                        <Column dataField="Classcode" caption="Class - Item" />
                        <Column dataField="LotCode" caption="Lot" />
                        <Column
                            dataField="Quantityordered"
                            caption="Ordered"
                            dataType="number"
                            format={{ type: "fixedPoint", precision: 2 }}
                        />
                        <Column
                            dataField="Quantityshipped"
                            caption="Shipped"
                            dataType="number"
                            format={{ type: "fixedPoint", precision: 2 }}
                        />
                        <Column
                            dataField="QuantityBackOrder"
                            caption="Back Order"
                            dataType="number"
                            format={{ type: "fixedPoint", precision: 2 }}
                        />
                        <Column
                            dataField="UnitPrice"
                            caption="Unit Price"
                            dataType="number"
                            format={{ type: "currency", precision: 4, currency: "USD", useGrouping: true }}
                        />
                        <Column
                            dataField="Amount"
                            caption="Amount"
                            dataType="number"
                            format={{ type: "currency", precision: 2, currency: "USD", useGrouping: true }}
                        />
                    </DataGrid>
                )}
            </Popup>

            <Popup
                visible={showUnitPricePopup}
                onHiding={() => setShowUnitPricePopup(false)}
                dragEnabled={false}
                showTitle={true}
                title="Enter Unit Price"
                width={360}
                height="auto"
                showCloseButton={true}
            >
                <div style={{ padding: 16 }}>
                    <div style={{ marginBottom: 12 }}>
                        <span style={{ marginRight: 8 }}>Unit Price</span>
                        <NumberBox
                            value={unitPriceInput}
                            onValueChanged={(e) => setUnitPriceInput(e.value)}
                            format={{ type: "fixedPoint", precision: 4 }}
                            showSpinButtons={true}
                            width="100%"
                        />
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <Button
                            text="Cancel"
                            onClick={() => setShowUnitPricePopup(false)}
                            disabled={isApplyingUnitPrice}
                        />
                        <Button
                            text="Accept"
                            type="default"
                            onClick={applyUnitPrice}
                            disabled={isApplyingUnitPrice}
                        />
                    </div>
                </div>
            </Popup>
        </>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(TransactionDetailGridPopup);
